/**
 * Книга нормативов ЕТС деревом (AK-723).
 *
 * Структура утверждена founder 26.08 дословно:
 *
 *     Культивация
 *     |- глубина 6-8 см
 *     |-- МТЗ-2022
 *     |--- КСК-12
 *     |---- 12 м
 *
 * Промежуточные узлы показывают справку приглушённо — число строк под узлом и
 * ДИАПАЗОН расценки. Не медиану: она отвергнута, потому что в книге нет цены
 * работы — есть цена работы конкретным агрегатом.
 *
 * Экономика живёт на уровне «работа × параметры»: там позиция бюджета, там
 * бейдж ручной цены.
 *
 * Строка книги ПРАВИТСЯ (AK-772, founder 28.08). Прежняя формулировка «глубже
 * книга read-only — строку ЕТС правит финансист в своём файле» отменена: две
 * главные цифры (расценка и топливо) правятся прямо в листе, остальной белый
 * список — через меню строки. Сама правка живёт в `NormRowEditing.tsx`, здесь
 * только место, куда она встроена.
 */
import { useCallback, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { formatFuelRate, formatMoneyCell, formatMoneyNumber } from '@/lib/planning/money';
import { useTextFilterDraft } from '@/lib/filters/useTextFilterDraft';
import {
  expandPathsFromHits,
  pathKey,
  useNormsBooks,
  useNormsSearch,
  useNormsTree,
  usePatchNorm,
  type NormsBookRow,
  type NormsPath,
  type NormsTreeNode,
} from '@/lib/planning/laborNorms';
import {
  ArchiveNormDialog,
  NormEditDialog,
  NormNumberCell,
  NormRowMenu,
  OverriddenBadge,
  isFieldOverridden,
} from './NormRowEditing';

/** Подпись единицы: символ справочника, иначе написание из книги. */
function unitLabel(symbol: string | null, raw: string | null): string {
  return symbol || raw || 'ед';
}

/**
 * «45,41 – 225,42 ₽/га» под узлом. Одна цифра, когда разброса нет: диапазон из
 * одного значения читается как ошибка вёрстки.
 */
export function nodeRangeLabel(node: NormsTreeNode): string {
  // Единица берётся из справочника, а если он не сматчен — из написания книги.
  // «272,48 ₽» без единицы — цифра без смысла: платят за час, а не за перегон.
  const raw = node.unit_symbol || node.unit_raw;
  const unit = raw ? `/${raw}` : '';
  if (node.pay_min === null || node.pay_max === null) return '';
  if (node.pay_min >= node.pay_max) return `${formatMoneyNumber(node.pay_min)} ₽${unit}`;
  return `${formatMoneyNumber(node.pay_min)} – ${formatMoneyNumber(node.pay_max)} ₽${unit}`;
}

/** «3 строки» / «21 строка» — счёт по-русски, без «3 строк(и)». */
export function rowsCountLabel(n: number): string {
  const last2 = n % 100;
  const last = n % 10;
  if (last2 >= 11 && last2 <= 14) return `${n} строк`;
  if (last === 1) return `${n} строка`;
  if (last >= 2 && last <= 4) return `${n} строки`;
  return `${n} строк`;
}

function pathWith(path: NormsPath, level: string, key: string): NormsPath {
  return { ...path, [level === 'work' ? 'work_id' : level]: key };
}

/**
 * Общая единица столбца — если она одна на все строки. Тогда «₽/га» уезжает в
 * шапку, а в ячейках остаются голые числа: повторять единицу в каждой строке
 * при одинаковой единице — шум, который мешает сравнивать цифры глазами.
 * Единицы разные — возвращаем null, и каждая ячейка подписывается сама.
 */
export function sharedUnit(
  rows: NormsBookRow[],
  pick: (row: NormsBookRow) => string | null,
): string | null {
  const units = new Set(rows.map((row) => pick(row) || ''));
  if (units.size !== 1) return null;
  return [...units][0] || null;
}

/**
 * Лист дерева — цифры строки книги, с ОБЕИМИ единицами (оплата и ГСМ).
 *
 * Правка (AK-772, founder 28.08): расценка и топливо правятся ПО МЕСТУ, всё
 * остальное — через меню строки. Право одно и то же, `budget_catalog:U`; без
 * него таблица остаётся ровно такой, какой была, — цифрами для чтения.
 *
 * Правка требует СЕЗОНА: бэк кладёт её в лог конкретного сезона и не угадывает
 * его. Экран без сезона правку не показывает, а не «сохраняет куда-нибудь».
 */
function BookRows({
  rows,
  canWrite,
  seasonId,
}: {
  rows: NormsBookRow[];
  canWrite: boolean;
  seasonId?: string | null;
}) {
  const payUnit = sharedUnit(rows, (row) => row.unit_symbol || row.unit_raw);
  const fuelUnit = sharedUnit(rows, (row) => row.fuel_unit_symbol || row.fuel_unit_raw);
  const editable = canWrite && !!seasonId;
  const patch = usePatchNorm(seasonId ?? '');
  const [editRow, setEditRow] = useState<string | null>(null);
  const [archiveRow, setArchiveRow] = useState<NormsBookRow | null>(null);
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Трактор / комбайн</TableHead>
            <TableHead>С/х машина</TableHead>
            <TableHead className="w-[9%] text-right">Ширина, м</TableHead>
            <TableHead className="w-[11%] text-right">Норма за смену</TableHead>
            <TableHead className="w-[13%] text-right">
              {payUnit ? `Расценка, ₽/${payUnit}` : 'Расценка'}
            </TableHead>
            <TableHead className="w-[12%] text-right">
              {fuelUnit ? `Топливо, л/${fuelUnit}` : 'Топливо'}
            </TableHead>
            <TableHead className="w-[15%]">Строка книги</TableHead>
            {editable && <TableHead className="w-[5%]" />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>
                {row.prime_mover ?? row.executor_hint_raw ?? '—'}
                {row.machines_ambiguous && (
                  <Badge
                    variant="outline"
                    className="ml-2"
                    title="в книге перечислено несколько марок — взаимозаменяемые они или состав сцепки, источник не говорит"
                  >
                    марка не однозначна
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                {row.implement ?? '—'}
                {row.implement_count && row.implement_count > 1 ? ` × ${row.implement_count}` : ''}
              </TableCell>
              <TableCell className="text-right tabular-nums">{row.width_m ?? '—'}</TableCell>
              <TableCell className="text-right tabular-nums">{row.norm_per_shift ?? '—'}</TableCell>
              <TableCell className="text-right tabular-nums">
                <NormNumberCell
                  value={row.pay_per_unit}
                  label={`Расценка — ${row.prime_mover ?? '—'}${row.implement ? ` · ${row.implement}` : ''}`}
                  format={formatMoneyCell}
                  overridden={isFieldOverridden(row, 'total_pay_per_unit')}
                  disabled={!editable}
                  onCommit={(next) =>
                    patch.mutateAsync({ id: row.id, patch: { total_pay_per_unit: next } })
                  }
                />
                {!payUnit && (
                  <span className="block text-xs text-muted-foreground">
                    ₽/{unitLabel(row.unit_symbol, row.unit_raw)}
                  </span>
                )}
              </TableCell>
              {/* Единица ГСМ своя: у перегона оплата в ₽/час, а топливо в л/км.
                  Это не рассинхрон, а две колонки книги. */}
              <TableCell className="text-right tabular-nums">
                <NormNumberCell
                  value={row.fuel_rate}
                  label={`Топливо — ${row.prime_mover ?? '—'}${row.implement ? ` · ${row.implement}` : ''}`}
                  format={formatFuelRate}
                  overridden={isFieldOverridden(row, 'fuel_rate')}
                  disabled={!editable}
                  onCommit={(next) => patch.mutateAsync({ id: row.id, patch: { fuel_rate: next } })}
                />
                {!fuelUnit && (
                  <span className="block text-xs text-muted-foreground">
                    л/{unitLabel(row.fuel_unit_symbol, row.fuel_unit_raw)}
                  </span>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {row.source_sheet ?? '—'}
                {row.source_row ? `, строка ${row.source_row}` : ''}
                {/* Метка «изменено вручную» видна ВСЕМ, включая читателя без
                    права правки: расхождение с книгой — это факт о данных, а не
                    свойство кнопки. */}
                <OverriddenBadge row={row} />
              </TableCell>
              {editable && (
                <TableCell className="text-right">
                  <NormRowMenu
                    row={row}
                    onEdit={() => setEditRow(row.id)}
                    onArchive={() => setArchiveRow(row)}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {editRow && seasonId && (
        <NormEditDialog normId={editRow} seasonId={seasonId} onClose={() => setEditRow(null)} />
      )}
      {archiveRow && seasonId && (
        <ArchiveNormDialog
          row={archiveRow}
          seasonId={seasonId}
          onClose={() => setArchiveRow(null)}
        />
      )}
    </>
  );
}

function TreeLevel({
  workKind,
  workbook,
  sheet,
  seasonId,
  path,
  depth,
  q,
  autoExpand,
  canWrite,
}: {
  workKind: 'field' | 'transport';
  workbook?: string;
  sheet?: string;
  seasonId?: string | null;
  path: NormsPath;
  depth: number;
  q?: string;
  autoExpand: Set<string>;
  canWrite: boolean;
}) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const level = useNormsTree({ workKind, workbook, sheet, seasonId, path, q });

  if (level.isPending) {
    return <div className="py-2 pl-6 text-sm text-muted-foreground">Читаем книгу…</div>;
  }
  if (level.isError) {
    return (
      <div className="py-2 pl-6 text-sm text-destructive">
        Не удалось прочитать книгу: {level.error.message}
      </div>
    );
  }
  if (level.data.level === 'leaf') {
    return (
      <div className="space-y-2 py-2 pl-6">
        <BookRows rows={level.data.rows} canWrite={canWrite} seasonId={seasonId} />
        {/* Подпись обещает ровно то, что система делает. Прежняя («строки пока
            только читаются») врала бы сразу после AK-772, а обещание «правка
            появится следующим шагом» уже исполнено. Главное здесь — вторая
            фраза: экономист правит расценку и должен ЗНАТЬ, что следующая
            заливка книги финансистов её не съест (ADR-073 D12). */}
        {canWrite && seasonId && (
          <p className="text-xs text-muted-foreground">
            Расценку и расход топлива правьте прямо в таблице, остальные поля —
            через меню строки. Правки сохраняются в системе; перезаливка книги
            финансистов их не затирает.
          </p>
        )}
        {canWrite && !seasonId && (
          <p className="text-xs text-muted-foreground">
            Правка строки записывается в конкретный сезон — выберите сезон, чтобы
            менять расценки.
          </p>
        )}
      </div>
    );
  }
  if (!level.data.nodes.length) {
    return <div className="py-2 pl-6 text-sm text-muted-foreground">Ничего не нашлось.</div>;
  }

  const currentLevel = level.data.level;
  return (
    <ul className={cn('space-y-1', depth > 0 && 'border-l border-border pl-4')}>
      {level.data.nodes.map((node) => {
        // Поиск раскрывает ветки сам: совпадение внутри свёрнутого узла для
        // человека не существует.
        // Путь, а не имя: «глубина 6-8 см» есть у Культивации, Дискования и
        // Боронования, и раскрытие по имени развернуло бы пол-дерева.
        const nodePath = pathKey(path, node.key);
        const expanded = open.has(node.key) || (!!q && autoExpand.has(nodePath));
        const range = nodeRangeLabel(node);
        const manualPositions = node.positions.filter((p) => p.planned_price !== null);
        const toggle = () =>
          setOpen((cur) => {
            const next = new Set(cur);
            if (next.has(node.key)) next.delete(node.key);
            else next.add(node.key);
            return next;
          });
        return (
          <li key={node.key}>
            {/* Кликабельна ВСЯ строка, а не шевron 24×24: строка подсвечивается
                на hover, то есть уже обещает нажатие — и обязана его принимать.
                role=button + Enter/Space, потому что <button> нельзя вложить в
                <button> (внутри строки живут пилюли экономики). */}
            <div
              role="button"
              tabIndex={0}
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Свернуть' : 'Раскрыть'} — ${node.label}`}
              onClick={toggle}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggle();
                }
              }}
              className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronRight
                aria-hidden
                className={cn(
                  'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                  expanded && 'rotate-90',
                )}
              />
              <span className="font-medium">{node.label}</span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {rowsCountLabel(node.rows_count)}
                {range ? ` · ${range}` : ''}
              </span>
              {/* Экономика — только на уровне «работа × параметры». Клик по
                  пилюле не сворачивает узел: это отдельная зона. */}
              {currentLevel === 'params' && manualPositions.length > 0 && (
                <span
                  className="flex flex-wrap gap-1"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  role="presentation"
                >
                  {manualPositions.map((position) => (
                    <Badge
                      key={position.id}
                      variant="outline"
                      title="цену вписал человек; заливка из книги её не трогает"
                    >
                      {/* Единица обязательна: «Подвоз семян» ₽/га и ₽/т — это
                          полевой подвоз против транспортного рейса, разные
                          деньги. Без единицы две позиции читаются как дубль. */}
                      цена задана вручную: {formatMoneyCell(position.planned_price)} ₽
                      {position.unit_symbol ? `/${position.unit_symbol}` : ''}
                    </Badge>
                  ))}
                </span>
              )}
            </div>
            {expanded && (
              <div className="ml-3">
                <TreeLevel
                  workKind={workKind}
                  workbook={workbook}
                  sheet={sheet}
                  seasonId={seasonId}
                  path={pathWith(path, currentLevel, node.key)}
                  depth={depth + 1}
                  q={q}
                  autoExpand={autoExpand}
                  canWrite={canWrite}
                />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function NormsTree({
  seasonId,
  canWrite = false,
}: {
  seasonId?: string | null;
  canWrite?: boolean;
}) {
  const books = useNormsBooks();
  const [book, setBook] = useState<string | null>(null);
  const [sheet, setSheet] = useState<string | null>(null);
  // Поиск с ПАУЗОЙ. Без неё каждая буква уходит в запрос НА КАЖДЫЙ раскрытый
  // уровень: пять открытых уровней × «КСК-12» — под сорок запросов на одно
  // слово. Хук общий (`lib/filters`), тот же, что на списке полей.
  const [term, setTerm] = useState('');
  const pushTerm = useCallback((value: string) => setTerm(value), []);
  const [draft, setDraft] = useTextFilterDraft(term, pushTerm);

  // Книга = кластер (ADR-073 D14). Показываем ОДНУ книгу за раз: смешение
  // трёх регионов в одном узле не «некрасиво», а неверно — общий диапазон
  // «Пахоты» не существует ни в одной книге.
  const bookOptions = useMemo(() => {
    const seen = new Map<string, { workbook: string; label: string; rows: number }>();
    for (const tab of books.data ?? []) {
      const prev = seen.get(tab.workbook);
      seen.set(tab.workbook, {
        workbook: tab.workbook,
        label: tab.book_label,
        rows: (prev?.rows ?? 0) + tab.rows_count,
      });
    }
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label, 'ru'));
  }, [books.data]);

  const activeBook = book ?? bookOptions[0]?.workbook ?? null;
  const sheets = useMemo(
    () => (books.data ?? []).filter((b) => b.workbook === activeBook),
    [books.data, activeBook],
  );
  const active = sheets.find((s) => s.sheet === sheet) ?? sheets[0];

  const hits = useNormsSearch({
    q: term,
    workbook: active?.workbook,
    sheet: active?.sheet,
  });
  const autoExpand = useMemo(() => expandPathsFromHits(hits.data ?? []), [hits.data]);

  if (books.isPending) {
    return <p className="text-sm text-muted-foreground">Читаем книги нормативов…</p>;
  }
  if (!books.data?.length) {
    return (
      <p className="text-sm text-muted-foreground">
        Нормативы ЕТС ещё не загружены — дерево появится после загрузки книги.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {/* Сначала КНИГА (она же кластер), потом её листы — тот же порядок, что
          у экономиста в Excel: сначала открыть файл, потом вкладку. */}
      {bookOptions.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Книга нормативов:</span>
          <Tabs
            value={activeBook ?? undefined}
            onValueChange={(value) => {
              setBook(value);
              // Лист сбрасываем: «Полевые_ЕТС» одной книги и другой — разные
              // листы, и удерживать выбор по имени значит молча показать
              // чужой регион.
              setSheet(null);
            }}
          >
            <TabsList>
              {bookOptions.map((option) => (
                <TabsTrigger key={option.workbook} value={option.workbook} title={option.workbook}>
                  {option.label}
                  <span className="ml-2 text-xs tabular-nums">{option.rows}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      )}

      <Tabs value={active?.sheet ?? undefined} onValueChange={(value) => setSheet(value)}>
        <TabsList>
          {sheets.map((tab) => (
            <TabsTrigger key={tab.sheet} value={tab.sheet} title={tab.workbook}>
              {tab.sheet}
              <span className="ml-2 text-xs tabular-nums">{tab.rows_count}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Input
        value={draft}
        placeholder="Найти работу, трактор или сцепку — например «КСК-12»"
        aria-label="Поиск по книге нормативов"
        onChange={(e) => setDraft(e.target.value)}
        className="max-w-md"
      />

      {active && (
        <TreeLevel
          key={`${active.workbook}::${active.sheet}`}
          workKind={active.work_kind}
          workbook={active.workbook}
          sheet={active.sheet}
          seasonId={seasonId}
          path={{}}
          depth={0}
          q={term.length >= 2 ? term : undefined}
          autoExpand={autoExpand}
          canWrite={canWrite}
        />
      )}
    </div>
  );
}
